import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  LoadingBlock,
  PageHeader,
  Textarea,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { useOrg, useUpdateOrg } from "../lib/queries";
import type { BillingIdentity } from "../lib/types";

/**
 * Who this organization is on a billing document.
 *
 * A page rather than a section on Billing, and a *separate* identity from the
 * organization's name. The name in the switcher is a label somebody picked and
 * can change on a whim; a document a finance team books needs the legal entity
 * and where it is registered. Conflating them means an invoice that says "Acme
 * staging" because that is what the org was called the day it was created.
 *
 * **Nothing here computes tax.** The Terms say it plainly — the platform applies
 * no tax handling, currency is single and global, and the customer is
 * responsible for their own. `taxId` is recorded because the customer's own
 * registration number belongs on the document *they* file, not because anything
 * derives from it. A rate field here would be the first half of contradicting a
 * published term.
 */

/** The form's shape: every field a string, because that is what inputs hold. */
type Form = { name: string; email: string; address: string; country: string; taxId: string };

const EMPTY: Form = { name: "", email: "", address: "", country: "", taxId: "" };

/**
 * A stored identity as form values.
 *
 * Null becomes `""` rather than the organization's display name. A prefilled
 * legal name that was never entered is the failure this page exists to prevent:
 * it reads as confirmed, and it is a guess.
 */
export function formFor(billing: BillingIdentity): Form {
  return {
    name: billing.name ?? "",
    email: billing.email ?? "",
    address: billing.address ?? "",
    country: billing.country ?? "",
    taxId: billing.taxId ?? "",
  };
}

/**
 * What to send: only the fields that differ from what is stored.
 *
 * The API tells absent from null apart — leave alone against clear — so sending
 * the whole form on every save would rewrite fields nobody touched, and each
 * rewrite is an audit entry claiming a change that did not happen.
 */
export function changedFields(form: Form, baseline: Form): Partial<Record<keyof Form, string>> {
  const changed: Partial<Record<keyof Form, string>> = {};
  for (const key of Object.keys(form) as (keyof Form)[]) {
    if (form[key] !== baseline[key]) changed[key] = form[key];
  }
  return changed;
}

/** Whether an identity is complete enough to head a document. */
export function isBillable(billing: BillingIdentity): boolean {
  // Name and address only. A country is useful and a tax id is jurisdictional,
  // but a document with a legal name and an address is one a bookkeeper can
  // accept; without either it is a note.
  return Boolean(billing.name?.trim() && billing.address?.trim());
}

export function BillingDetailsPage() {
  const { t } = useTranslation("billing");
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  // Owner only, matching the API: this is the identity the organization is
  // invoiced under, not a preference.
  const canEdit = currentOrg?.role === "owner";

  const org = useOrg(orgId);
  const update = useUpdateOrg(orgId);

  /*
   * Seeded at construction when the organization is already cached, and again
   * by the effect below when it arrives later.
   *
   * The effect alone left the whole form — and the document preview beside it —
   * empty for one paint on every visit where the query was already warm. Brief,
   * and it reads as a page that lost what was in it, which on a form about a
   * legal identity is the worst possible first impression.
   */
  const stored = org.data ? formFor(org.data.billing) : EMPTY;
  const [form, setForm] = useState<Form>(stored);
  const [baseline, setBaseline] = useState<Form>(stored);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  /*
   * Re-seeded whenever the organization changes — a save returns the stored
   * row, which is what makes the next diff correct rather than comparing
   * against what was on screen before the request.
   */
  useEffect(() => {
    if (!org.data) return;
    const next = formFor(org.data.billing);
    setForm(next);
    setBaseline(next);
  }, [org.data]);

  const ready = form.name.trim() !== "" && form.address.trim() !== "";
  const changed = changedFields(form, baseline);
  const dirty = Object.keys(changed).length > 0;

  async function save() {
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({
        billingName: changed.name,
        billingEmail: changed.email,
        billingAddress: changed.address,
        billingCountry: changed.country,
        taxId: changed.taxId,
      });
      setSaved(true);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("details.failed"));
    }
  }

  if (org.isLoading) return <LoadingBlock />;
  if (org.isError) return <Alert>{t("details.loadFailed")}</Alert>;

  const field = (key: keyof Form) => ({
    value: form[key],
    disabled: !canEdit,
    onChange: (event: { target: { value: string } }) => {
      setSaved(false);
      setForm((previous) => ({ ...previous, [key]: event.target.value }));
    },
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("details.title")}
        description={t("details.description")}
      />

      {!canEdit && (
        <Alert tone="info">
          {t("details.readOnly")}
        </Alert>
      )}

      {error && <Alert tone="error">{error}</Alert>}
      {saved && !dirty && <Alert tone="success">{t("details.saved")}</Alert>}

      {/*
        Two columns, because the form and the thing it produces are one subject.
        Stacked, the preview sat below the fold — so the only moment it was
        useful, watching a legal name land where a bookkeeper will read it, was
        the one moment it could not be seen. The right column is sticky on wide
        screens for the same reason, and the layout collapses to one column
        under `lg` where sticky would pin a panel over the field being typed in.
      */}
      <div className="grid gap-6 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          <Card>
            <CardHeader
              title={t("details.billedTo")}
              description={t("details.billedToHint")}
            />
            <div className="space-y-5 p-5">
              <Field
                label={t("details.name")}
                htmlFor="billing-name"
                hint={t("details.nameHint")}
              >
                <Input
                  id="billing-name"
                  placeholder={t("details.namePlaceholder")}
                  {...field("name")}
                />
              </Field>

              <Field
                label={t("details.address")}
                htmlFor="billing-address"
                hint={t("details.addressHint")}
              >
                <Textarea id="billing-address" rows={4} {...field("address")} />
              </Field>

              <div className="grid gap-5 sm:grid-cols-2">
                <Field
                  label={t("promo.country")}
                  htmlFor="billing-country"
                  hint={t("details.countryHint")}
                >
                  <Input
                    id="billing-country"
                    maxLength={2}
                    placeholder="XX"
                    className="uppercase"
                    {...field("country")}
                  />
                </Field>

                <Field
                  label={t("details.taxId")}
                  htmlFor="billing-tax-id"
                  hint={t("details.taxIdHint")}
                >
                  <Input
                    id="billing-tax-id"
                    placeholder={t("details.taxIdPlaceholder")}
                    {...field("taxId")}
                  />
                </Field>
              </div>

              <Field
                label={t("details.email")}
                htmlFor="billing-email"
                hint={t("details.emailHint")}
              >
                <Input
                  id="billing-email"
                  type="email"
                  placeholder={t("details.emailPlaceholder")}
                  {...field("email")}
                />
              </Field>
            </div>

            {canEdit && (
              <div className="flex items-center justify-end gap-2 border-t border-line px-5 py-3.5">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!dirty || update.isPending}
                  onClick={() => setForm(baseline)}
                >
                  {t("details.discard")}
                </Button>
                <Button
                  size="sm"
                  disabled={!dirty}
                  loading={update.isPending}
                  onClick={() => void save()}
                >
                  {t("details.save")}
                </Button>
              </div>
            )}
          </Card>

          {/*
            A note rather than an `Alert`. That component defaults to the error
            tone and carries `role="alert"`, so this rendered red with a warning
            icon and was announced to a screen reader as something that had just
            gone wrong. It is neither: it is a standing explanation of how the
            platform bills, sitting where somebody entering a tax number would
            ask the question.
          */}
          <div className="rounded-md border border-line bg-surface px-4 py-3 text-[13px] leading-6 text-fg-muted">
            {t("details.tax")}
          </div>
        </div>

        <div className="lg:col-span-2">
          <div className="lg:sticky lg:top-6">
            <Card>
              <CardHeader title={t("details.preview")} description={t("details.previewDescription")} />

              {/*
                Shown light, because that is what it is. This is a sheet of
                paper — the invoice is generated as a standalone document and
                read months later in a browser that has never seen this
                stylesheet — and rendering it in the dashboard's dark palette
                would show a page that does not exist. The literal colours are
                the exception the semantic tokens do not cover: they belong to
                the document, not to the interface around it.
              */}
              <div className="p-5">
                <div className="rounded-sm bg-white p-5 text-[#1a1a1a] shadow-lg shadow-black/40">
                  <p className="text-[10px] font-semibold tracking-[0.08em] text-[#777] uppercase">
                    {t("details.billedTo")}
                  </p>
                  {ready ? (
                    <div className="mt-2 text-[13px] leading-6">
                      <p className="font-semibold">{form.name}</p>
                      {form.address.split("\n").map((line, index) => (
                        <p key={`${line}-${index}`}>{line}</p>
                      ))}
                      {form.email && <p>{form.email}</p>}
                      {form.taxId && (
                        <p className="mt-2 text-[12.5px] text-[#555]">
                          {t("details.taxLine", { taxId: form.taxId })}
                        </p>
                      )}
                    </div>
                  ) : (
                    /* The same block with nothing in it, so the gap is shown
                       where it will appear rather than described in a sentence
                       somewhere else on the page. */
                    <div className="mt-2 space-y-2" aria-hidden="true">
                      <div className="h-3 w-3/5 rounded-sm bg-[#e5e5e5]" />
                      <div className="h-3 w-4/5 rounded-sm bg-[#efefef]" />
                      <div className="h-3 w-2/5 rounded-sm bg-[#efefef]" />
                    </div>
                  )}
                </div>
              </div>

              {/*
                What the preview means, in the one sentence somebody needs: can
                this organization be issued an invoice or not. It is the page's
                whole purpose and was previously answerable only by trying.
              */}
              <div className="flex items-start gap-2.5 border-t border-line px-5 py-3.5 text-[13px]">
                <span
                  aria-hidden="true"
                  className={`mt-1.5 size-1.5 shrink-0 rounded-full ${ready ? "bg-mint" : "bg-warn"}`}
                />
                <p className="text-fg-muted">
                  {ready ? (
                    <>
                      <span className="text-mint">{t("details.ready")}</span>{" "}
                      {t("details.readyBody")}
                    </>
                  ) : (
                    <>
                      <span className="text-warn">{t("details.notReady")}</span>{" "}
                      {t("details.notReadyBody")}
                    </>
                  )}
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <p className="text-xs leading-6 text-fg-subtle">
        <Trans
          ns="billing"
          i18nKey="details.footer"
          components={{
            billing: <Link to="/settings/billing" className="text-accent underline underline-offset-2" />,
          }}
        />
      </p>
    </div>
  );
}
